/**
 * Формат графа сценария. Доступен и отдельным входом пакета
 * (`@rk/contracts/graph`), а не только через `index`: админка компилируется
 * без глобалов Node, а через `index` в её программу попали бы порты с
 * `import type { Readable } from 'node:stream'`. Редактор обязан проверять
 * граф тем же `parseGraph`, что и служба, — вторая проверка разошлась бы с
 * первой.
 */
import { z } from 'zod'
import { CHANNEL_PROVIDERS } from './providers.js'
import { AppError } from './errors.js'

export class GraphFormatError extends AppError {
  readonly code = 'GRAPH_FORMAT_INVALID'
}

// ---------- Описание ноды ----------

export const NodePortSchema = z.object({
  name: z.string().min(1),
  displayName: z.string().min(1),
  /**
   * 'main' — обычный поток элементов; 'error' — выход ошибок ноды
   * (FR-PIPE-017). Типов пока два: третий добавляется, когда появится
   * потребитель, а не заранее — тип без потребителя нечем проверить.
   */
  portType: z.enum(['main', 'error']).default('main'),
})

const ParameterOptionSchema = z.object({
  value: z.union([z.string(), z.number(), z.boolean()]),
  label: z.string().min(1),
})

export const NodeParameterSchema = z.object({
  name: z.string().min(1),
  displayName: z.string().min(1),
  type: z.enum([
    'string',
    'number',
    'boolean',
    'options',
    'multiOptions',
    'json',
    'dateTime',
    'collection',
    'credentials',
    /**
     * Идентификатор инстанса канала. Отдельный тип, а не строка: иначе
     * проверка «триггер WhatsApp без существующего инстанса блокирует
     * публикацию» (FR-PIPE-005) опиралась бы на имя типа ноды, зашитое
     * в валидатор, — а нода обязана описываться декларативно (FR-NODE-001).
     */
    'instance',
    /**
     * Идентификатор другого сценария того же арендатора — параметр ноды
     * подсценария. Отдельный тип по той же причине, что и инстанс:
     * проверка «сценарий, которого нет, блокирует публикацию» обязана
     * работать по описанию ноды, а не по её имени, зашитому в валидатор
     * (FR-PIPE-021, FR-NODE-001).
     */
    'workflow',
  ]),
  description: z.string().optional(),
  required: z.boolean().default(false),
  default: z.unknown().optional(),
  options: z.array(ParameterOptionSchema).optional(),
  /**
   * Какой формы значение ждёт нода от параметра типа `json`. Только для
   * него: у остальных типов форма следует из самого типа — `number` это
   * число, `options` это одно из объявленных значений, — а `json` означает
   * «что угодно, лишь бы разобралось», и этого мало.
   *
   * Из-за этого «мало» объект вместо списка публиковался со словами
   * «Замечаний нет» и падал уже в прогоне: «У узла „Список из выгрузки“
   * поля заданы не списком». Тот же класс отказа, что и с невозможным
   * выражением (FR-PIPE-040) — портал одобрил, прогон промолчал, — и
   * лечится он так же: нода объявляет форму, валидатор её сверяет
   * (FR-PIPE-041).
   *
   * Объявляется описанием ноды, а не выводится валидатором из имени типа:
   * список нод в валидаторе — ровно то, что запрещает FR-NODE-001.
   */
  shape: z.enum(['list', 'object']).optional(),
  /** Показывать параметр, только когда другие параметры имеют указанные значения. */
  displayIf: z.record(z.string(), z.array(z.unknown())).optional(),
  placeholder: z.string().optional(),
})

export const NodeDefinitionSchema = z.object({
  type: z.string().min(1),
  version: z.number().int().min(1),
  displayName: z.string().min(1),
  /**
   * Раздел палитры. Каналы разнесены по разделам, а не собраны в один
   * «мессенджеры»: у номеров разных каналов разные адреса и разные
   * возможности, и держать их узлы вперемешку значит предлагать выбрать
   * не тот.
   */
  group: z.enum([
    'trigger',
    'flow',
    'data',
    'integration',
    'whatsapp',
    'telegram',
    'ai',
    'utility',
  ]),
  description: z.string().min(1),
  icon: z.string().optional(),
  inputs: z.array(NodePortSchema),
  outputs: z.array(NodePortSchema).min(1),
  parameters: z.array(NodeParameterSchema),
  credentialTypes: z.array(z.string()).default([]),
  /**
   * Нода способна завершить итерацию цикла. Объявляется описанием, а не
   * выводится валидатором из имени типа: цикл без такой ноды крутился бы
   * вечно, и публикацию нужно отклонить (FR-PIPE-025).
   */
  terminatesLoop: z.boolean().default(false),
  /**
   * Возможность провайдера, без которой нода бессмысленна: имя поля
   * `ChannelCapabilities`. Объявляется здесь, потому что редактор не должен
   * предлагать к выбору ноду, которую данный провайдер исполнить не может
   * (FR-NODE-009), а знать об этом по имени типа — значит зашивать список
   * нод в редактор.
   */
  requiresCapability: z.string().optional(),
  /**
   * Канал, к номерам которого относится нода. Объявляется описанием по
   * той же причине, что и `requiresCapability`: редактор не знает имён
   * конкретных нод (FR-NODE-001), а зашить список в него значит завести
   * второе место, где перечислены ноды каналов.
   *
   * Работает в трёх местах: нода исчезает из палитры, когда у арендатора
   * нет ни одного номера этого канала; выпадающий список инстансов сужен
   * по нему; узел, указывающий на номер другого канала, не публикуется
   * (FR-NODE-011, FR-PIPE-042). Без последней проверки опубликованный
   * сценарий отказывал бы в прогоне, а портал говорил «Замечаний нет».
   */
  channelProvider: z.enum(CHANNEL_PROVIDERS).optional(),
  /**
   * Узел без выбранного credential не публикуется (FR-PIPE-029). Не общее
   * правило для всех, у кого объявлены типы: HTTP-нода ходит и в открытые
   * службы, где ключ не нужен, — а нода ИИ без ключа не сделает ничего.
   */
  credentialsRequired: z.boolean().optional(),
  /**
   * Образец данных срабатывания: то, что нода отдаёт в пробном прогоне
   * вместо настоящих данных (FR-UI-011). Список элементов — json каждого,
   * как их увидит следующий узел.
   *
   * Объявляется описанием ноды, а не списком в исполнителе, по той же
   * причине, что `awaitsCallbackIf`: движок не знает имён конкретных нод
   * (FR-NODE-001), а придумывать образец за ноду он тем более не может —
   * похож на правду только тот образец, который написан там же, где
   * описано само срабатывание.
   */
  sampleOutput: z.array(z.record(z.string(), z.unknown())).optional(),
  /**
   * При каких значениях параметров узел ждёт входящего вызова (FR-PIPE-011).
   * Форма та же, что у `displayIf`: параметр → допустимые значения.
   *
   * Объявляется описанием ноды, а не выводится исполнителем из имени типа.
   * Адрес возобновления выдаётся до первого узла — то есть до того, как
   * ждущий узел вообще начнёт исполняться, — и решать по имени значило бы
   * зашить список нод в движок (FR-NODE-001).
   */
  awaitsCallbackIf: z.record(z.string(), z.array(z.unknown())).optional(),
})

// ---------- Граф ----------

export const GraphNodeSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  typeVersion: z.number().int().min(1),
  name: z.string().min(1),
  position: z.tuple([z.number(), z.number()]),
  parameters: z.record(z.string(), z.unknown()).default({}),
  credentials: z.record(z.string(), z.string()).optional(),
  disabled: z.boolean().default(false),
  notes: z.string().optional(),
  onError: z.enum(['stop', 'continue', 'output']).default('stop'),
  retry: z
    .object({
      maxTries: z.number().int().min(1).max(10),
      waitMs: z.number().int().min(0),
    })
    .optional(),
})

export const GraphConnectionSchema = z.object({
  from: z.object({
    node: z.string().min(1),
    output: z.string().min(1),
    index: z.number().int().min(0),
  }),
  to: z.object({
    node: z.string().min(1),
    input: z.string().min(1),
    index: z.number().int().min(0),
  }),
})

export const GraphSchema = z.object({
  nodes: z.array(GraphNodeSchema),
  connections: z.array(GraphConnectionSchema),
})

export type NodePort = z.infer<typeof NodePortSchema>
export type NodeParameter = z.infer<typeof NodeParameterSchema>
export type NodeDefinition = z.infer<typeof NodeDefinitionSchema>
export type GraphNode = z.infer<typeof GraphNodeSchema>
export type GraphConnection = z.infer<typeof GraphConnectionSchema>
export type Graph = z.infer<typeof GraphSchema>

function fail(message: string, context: Record<string, unknown> = {}): never {
  throw new GraphFormatError(message, { context })
}

function formatZodIssues(error: z.ZodError): string {
  return error.issues
    .map((issue) => `  ${issue.path.join('.') || '(корень)'}: ${issue.message}`)
    .join('\n')
}

export function parseGraph(input: unknown): Graph {
  const parsed = GraphSchema.safeParse(input)
  if (!parsed.success) {
    fail(`Граф не соответствует формату:\n${formatZodIssues(parsed.error)}`, {
      issues: parsed.error.issues,
    })
  }

  const graph = parsed.data

  const ids = new Set<string>()
  const names = new Set<string>()
  for (const node of graph.nodes) {
    if (ids.has(node.id)) {
      fail(`Повторяющийся идентификатор ноды "${node.id}". Идентификаторы уникальны в графе.`, {
        nodeId: node.id,
      })
    }
    ids.add(node.id)

    if (names.has(node.name)) {
      fail(
        `Повторяющееся имя ноды "${node.name}". ` +
          'Имена уникальны, потому что по ним ноды адресуются в выражениях.',
        { nodeName: node.name },
      )
    }
    names.add(node.name)
  }

  for (const connection of graph.connections) {
    if (!ids.has(connection.from.node)) {
      fail(`Связь ссылается на несуществующую ноду-источник "${connection.from.node}".`, {
        nodeId: connection.from.node,
      })
    }
    if (!ids.has(connection.to.node)) {
      fail(`Связь ссылается на несуществующую ноду-приёмник "${connection.to.node}".`, {
        nodeId: connection.to.node,
      })
    }
  }

  return graph
}

export function parseNodeDefinition(input: unknown): NodeDefinition {
  const parsed = NodeDefinitionSchema.safeParse(input)
  if (!parsed.success) {
    fail(`Описание ноды не соответствует формату:\n${formatZodIssues(parsed.error)}`, {
      issues: parsed.error.issues,
    })
  }

  const definition = parsed.data

  // Порты адресуются по имени (`connection.from.output`), поэтому имя обязано
  // быть уникальным в своём наборе: два выхода "main" сделали бы адресацию
  // неоднозначной — валидатор не смог бы сказать, к какому из них ведёт
  // связь, а рантайм не знал бы, куда отдавать данные (FR-PIPE-023).
  // Наборы проверяются независимо: вход "main" и выход "main" — норма.
  for (const [kind, ports] of [
    ['входов', definition.inputs],
    ['выходов', definition.outputs],
  ] as const) {
    const names = new Set<string>()
    for (const port of ports) {
      if (names.has(port.name)) {
        fail(
          `Дублирующееся имя порта "${port.name}" среди ${kind} ноды "${definition.type}". ` +
            'Имена портов уникальны, потому что по ним порты адресуются в связях графа.',
          { type: definition.type, port: port.name },
        )
      }
      names.add(port.name)
    }
  }

  const seen = new Set<string>()
  for (const parameter of definition.parameters) {
    if (seen.has(parameter.name)) {
      fail(`Дублирующееся имя параметра "${parameter.name}" в ноде "${definition.type}".`, {
        type: definition.type,
        parameter: parameter.name,
      })
    }
    seen.add(parameter.name)

    const needsOptions = parameter.type === 'options' || parameter.type === 'multiOptions'
    if (needsOptions && (parameter.options === undefined || parameter.options.length === 0)) {
      fail(
        `Параметр "${parameter.name}" ноды "${definition.type}" имеет тип "${parameter.type}", ` +
          'но не объявляет варианты (options).',
        { type: definition.type, parameter: parameter.name },
      )
    }

    // Форма объявляется только там, где её иначе неоткуда узнать. У
    // `number` или `options` она уже следует из типа, и второе объявление
    // рядом с первым — это два места, которые разойдутся.
    if (parameter.shape !== undefined && parameter.type !== 'json') {
      fail(
        `Параметр «${parameter.name}» ноды «${definition.type}» объявляет форму ` +
          `«${parameter.shape}», но имеет тип «${parameter.type}»: форма объявляется ` +
          'только у json — у остальных типов она следует из самого типа.',
        { type: definition.type, parameter: parameter.name },
      )
    }
  }

  return definition
}
